import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DynamicPopup from './DynamicPopup';
import { POPUP_CONFIGS } from '../popupConfigs';
import builtInApiMap from '../definitions';

/**
 * Route wrapper for DynamicPopup.
 *
 * Drop straight into your App.jsx popup routes — NO props required.
 * The built-in apiMap and POPUP_CONFIGS are used automatically:
 *
 *   <Route path="/popup/:popupKey" element={<DynamicPopupRoute />} />
 *
 * To add project-specific popups alongside the built-ins:
 *
 *   const myConfigs = { 'send-offer': { title: 'Send Offer', fields: [...] } };
 *   const myApiMap  = { 'send-offer': { submit: async (values, ctx) => { ... } } };
 *   <Route path="/popup/:popupKey"
 *     element={<DynamicPopupRoute apiMap={myApiMap} extraConfigs={myConfigs} />} />
 *
 * Props:
 *   apiMap       — merged on top of the built-in handlers, so you only need to
 *                  list NEW keys; built-in popup APIs (delete, add-notes, …) keep working.
 *   extraConfigs — same merge pattern for configs.
 */
export default function DynamicPopupRoute({ apiMap = {}, extraConfigs = {} }) {
  // Merge: caller's entries win; built-ins fill the gaps.
  const mergedApiMap = { ...builtInApiMap, ...apiMap };
  const mergedConfigs = { ...POPUP_CONFIGS, ...extraConfigs };
  const { popupKey } = useParams();
  const navigate = useNavigate();
  const { state = {} } = useLocation();

  const config = mergedConfigs[popupKey];

  // Memoized so that router re-renders don't create a new object reference on every tick,
  // which would otherwise cause DynamicPopup's effects to re-run unnecessarily.
  const context = useMemo(
    () => ({
      selectedIds: state.selectedIds ?? [],
      selectedRecords: state.selectedRecords ?? [],
      moduleName: state.moduleName ?? '',
      navigate,
      ...(state.popupContext ?? {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, state.selectedIds, state.selectedRecords, state.moduleName, state.popupContext],
  );

  return (
    <DynamicPopup
      open
      config={config}
      context={context}
      onClose={() => navigate(-1)}
      api={mergedApiMap[popupKey] ?? {}}
    />
  );
}
